// Lead card (SPEC §15.1): who, where, when (prospect-local), limits, links, history, tasks. The AI pre-call brief was
// cut from the dial screen (REVIEW-2026-10-01: slow, paid per lead, and it duplicated the Business drawer).
import { useState } from 'preact/hooks';
import type { Business, Campaign, PreferredWindow, QueueItem } from '../../../src/shared/api';
import { recycleRoundLabel, recycleRoundTitle } from '../../../src/shared/api';
import { leadgenFieldsOf } from '../../../src/shared/api-core';
import { errorMessage } from '../api';
import { fmtDateTime, timeAgo, toast } from '../components/ui';
import { Field, LinkOut, useNow } from '../components/ui-extra';
import { verifyObservation } from './drawer/data';
import { DISPOSITION_META, fmtPhone, lineTypeLabel, stateNoteLine } from './labels';
import { LeadLinks, PhoneKindNote } from './LeadLinks';
import { LeadNumbers } from './LeadNumbers';
import { roleWords } from './numbers';
import { OBSERVATION_CHECK_TITLE, observationLine, observationUseGate, pickNiche } from './links';
import { confirmableGate, getDialer, replaceBusiness, toggleDrawer, updateWrap, type WrapUp } from './store';
import { fmtDuration, localBucket, localClock, zonedParts } from './time';

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function hhmmToMin(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return h * 60 + (m || 0);
}

function windowNow(windows: PreferredWindow[] | undefined, weekday: number, minutes: number): PreferredWindow | null {
  return (windows ?? []).find((w) => w.days.includes(weekday) && minutes >= hhmmToMin(w.start) && minutes < hhmmToMin(w.end)) ?? null;
}

function daysLabel(days: number[]): string {
  const sorted = [...days].sort();
  const contiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  return contiguous && sorted.length > 2 ? `${DAY[sorted[0]]}–${DAY[sorted[sorted.length - 1]]}` : sorted.map((d) => DAY[d]).join(', ');
}

const REASON_LABEL: Record<QueueItem['reason'], string> = { callback_due: 'Callback due', fresh: 'First call', retry: 'Retry' };

export function LeadCard(props: { item: QueueItem; campaign: Campaign | null; wrap: WrapUp; editable: boolean }) {
  const { item, wrap } = props;
  const b = item.business;
  const m = item.membership;
  const now = useNow(15000);
  let clock = item.localTime;
  let bucket: string | null = null;
  let pref: PreferredWindow | null = null;
  try {
    const p = zonedParts(new Date(now), b.tz);
    clock = localClock(new Date(now), b.tz);
    bucket = localBucket(p.hour);
    pref = windowNow(props.campaign?.windows, p.weekday, p.hour * 60 + p.minute);
  } catch { /* unknown tz string: fall back to the server's label */ }
  const drawer = getDialer().drawer; // Dialer re-renders on every store change, so this stays current
  const drawerMine = drawer.membershipId === m.id;
  const vmFull = m.vmCount >= item.campaign.maxVms;
  // SPEC §19 lead recycling: a recycle round carries its own cap (memberships.attempt_cap).
  const maxAttempts = m.attemptCap ?? item.campaign.maxAttempts;
  const attemptsLeft = maxAttempts - m.attempts;
  // PJ 2026-10-20: only soft attempt rules in the way = an amber warning, and Dial reads "Call again" (it asks first).
  const again = confirmableGate(item.dialGate);

  return (
    <div class="vd-lead">
      <div class="vd-lead-head">
        <div class="row" style="gap:6px;flex-wrap:wrap">
          <button class={`btn btn-sm ${drawerMine && drawer.open ? 'btn-ink' : ''}`} aria-pressed={drawerMine && drawer.open}
            title={drawerTitle(drawerMine ? drawer : null)}
            onClick={(e) => { (e.currentTarget as HTMLElement).blur(); toggleDrawer(); }}>
            Business <kbd>I</kbd>
          </button>
          {drawerMine && drawer.data?.header.tier ? (
            <span class="chip" title="Lead engine tier and priority for this campaign's offer">Tier {drawer.data.header.tier}{drawer.data.header.priority !== null ? ` · ${drawer.data.header.priority}` : ''}</span>
          ) : null}
          {item.noCampaign ? <span class="chip chip-info" title="A dial-pad call with no campaign: no lead row">No campaign</span> : null}
          <span class={`chip ${item.reason === 'callback_due' ? 'chip-info' : ''}`}>{REASON_LABEL[item.reason]}</span>
          {m.recycle ? <span class="chip chip-info" title={recycleRoundTitle(m.recycle)}>{recycleRoundLabel(m.recycle)}</span> : null}
          {b.niche ? <span class="chip">{b.niche}</span> : null}
          <span class="chip">{b.stateProv}, {b.country}</span>
          <span class={`chip ${b.lineType === 'mobile' ? 'chip-warn' : ''}`} title="From Twilio Lookup">{lineTypeLabel(b.lineType)}</span>
        </div>
      </div>
      {/* PJ 2026-10-09: "No website on file" / "No Google link on file" tags, "Find on Google Maps", "Search the area".
          At the top of the card since 2026-10-13: under the contact fields it sat below the fold on a laptop (1366x768: the
          card shows ~360 px), so Max never saw "Search the area" without scrolling. */}
      <div class="row vd-links">
        <LeadLinks business={b} niche={pickNiche(props.campaign?.niche, item.campaign.niche, b.niche)} />
        {b.emailSourceUrl ? <LinkOut href={b.emailSourceUrl}>Email source</LinkOut> : null}
      </div>
      {/* PJ 2026-10-23: a lead with the owner's number and the store line lists both (Dial calls the owner first, A the
          store), the owner's other stores and the called-before line; a lead with one number keeps its phone-kind note. */}
      {item.numbers && (item.numbers.owner || item.numbers.store) ? null : <PhoneKindNote business={b} />}
      <LeadNumbers item={item} />
      <ObservationNote key={b.id} business={b} researched={!!leadgenFieldsOf(b).researchAt || (drawerMine && !!drawer.data?.researched)} />

      <div class="vd-facts">
        <div><span class="eyebrow">Their time</span><b>{clock}</b>{bucket ? <span class="muted"> · {bucket}</span> : null}</div>
        <div>
          <span class="eyebrow">Best window</span>
          {pref ? <b class="vd-good">Now: {pref.label}</b>
            : props.campaign?.windows?.length
              ? <span class="muted">{props.campaign.windows.map((w) => `${w.label} (${daysLabel(w.days)} ${w.start}–${w.end})`).join('; ')}</span>
              : <span class="muted">—</span>}
        </div>
        {item.noCampaign ? (
          <div><span class="eyebrow">Calls, last 7 days</span><b>{m.attempts}</b><span class="muted"> · 3 a week, 2 a day, 4 h apart (more asks first); never over 3 a day</span></div>
        ) : (
          <>
            <div><span class="eyebrow">Attempts</span><b>{m.attempts}/{maxAttempts}</b>{attemptsLeft <= 1 ? <span class="muted"> · last one</span> : null}</div>
            <div><span class="eyebrow">Voicemails</span><b class={vmFull ? 'vd-bad' : ''}>{m.vmCount}/{item.campaign.maxVms}</b>{vmFull ? <span class="muted"> · no more VMs</span> : null}</div>
          </>
        )}
        <div><span class="eyebrow">Last attempt</span>{m.lastAttemptAt ? <span>{timeAgo(m.lastAttemptAt)}</span> : <span class="muted">never</span>}</div>
        <div><span class="eyebrow">City</span><span>{b.city ?? '—'}</span></div>
      </div>

      {again ? (
        <div class="vd-gate-note vd-gate-warn" role="note">
          <b>{again.meeting
            ? 'Meeting booked. You can still call (to confirm it or follow up): Dial asks you first.'
            : 'Called before. You can still call: Call again asks you first.'}</b>
          <ul>{item.dialGate.reasons.map((r) => <li>{r.message}</li>)}</ul>
          {item.dialGate.retryAt ? <span class="muted">Opens without asking {fmtDateTime(item.dialGate.retryAt)} your time.</span> : null}
        </div>
      ) : !item.dialGate.ok && item.dialGate.reasons.length ? (
        <div class="vd-gate-note">
          <b>Can't be dialed right now:</b>
          <ul>{item.dialGate.reasons.map((r) => <li>{r.message}</li>)}</ul>
          {item.dialGate.retryAt ? <span class="muted">Opens {fmtDateTime(item.dialGate.retryAt)} your time.</span> : null}
        </div>
      ) : null}

      <div class="vd-contacts">
        <Field label="Decision-maker">
          <input value={wrap.dmName} disabled={!props.editable} placeholder="Name (ask for it)" onInput={(e) => updateWrap({ dmName: e.currentTarget.value })} />
        </Field>
        <Field label="Role">
          <input value={wrap.dmRole} disabled={!props.editable} placeholder="Owner, GM…" onInput={(e) => updateWrap({ dmRole: e.currentTarget.value })} />
        </Field>
        <Field label="Gatekeeper">
          <input value={wrap.gatekeeperName} disabled={!props.editable} placeholder="Who answered" onInput={(e) => updateWrap({ gatekeeperName: e.currentTarget.value })} />
        </Field>
        <Field label="Best time to reach the DM">
          <input value={wrap.bestTimeNote} disabled={!props.editable} placeholder="e.g. mornings before 10" onInput={(e) => updateWrap({ bestTimeNote: e.currentTarget.value })} />
        </Field>
        <Field label="Email" class="vd-span2" hint={b.country === 'CA' ? 'Canada: email only an address they give you on this call when they ask for the email (CASL), never one from their website or a list.' : undefined}>
          <input type="email" value={wrap.email} disabled={!props.editable} placeholder="name@business.com" onInput={(e) => updateWrap({ email: e.currentTarget.value })} />
        </Field>
      </div>
      <p class="vd-field-hint" style="margin:2px 0 8px">These save with the call outcome.{b.dmSourceUrl ? ' ' : ''}{b.dmSourceUrl ? <LinkOut href={b.dmSourceUrl}>DM source</LinkOut> : null}</p>

      {item.openTasks.length ? (
        <div class="vd-section">
          <div class="eyebrow">Open tasks</div>
          <ul class="vd-list">
            {item.openTasks.map((t) => <li><b>{t.title}</b> <span class="muted">· due {fmtDateTime(t.dueAt)}</span>{t.note ? <div class="muted">{t.note}</div> : null}</li>)}
          </ul>
        </div>
      ) : null}

      <div class="vd-section">
        <div class="eyebrow">History (all campaigns)</div>
        {item.history.length === 0 ? <p class="muted" style="margin:4px 0">No calls yet.</p> : (
          <ul class="vd-list vd-history">
            {item.history.map((h) => (
              <li>
                <span class="mono">{fmtDateTime(h.createdAt)}</span>{' '}
                <b>{h.disposition ? DISPOSITION_META[h.disposition]?.label ?? h.disposition : h.direction === 'inbound' ? 'Inbound call' : 'No outcome logged'}</b>
                {h.vmType ? <span class="chip" style="margin-left:4px">VM {h.vmType}</span> : null}
                {h.numberRole ? <span class="chip" style="margin-left:4px" title="Which of the lead's numbers this call went to">{roleWords(h.numberRole)}</span> : null}
                <span class="muted">{h.durationSec ? ` · ${fmtDuration(h.durationSec)}` : ''}{h.userName ? ` · ${h.userName}` : ''}{h.campaignName ? ` · ${h.campaignName}` : ''}</span>
                {h.note ? <div class="vd-note">{h.note}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * "Seen online: <observation> · Check · Use in opener" (scripts audit F6): what the lead list saw, so the setter can use
 * it on the call. "Use in opener" (enabled once Check was clicked) is the verify endpoint the drawer uses; the updated
 * business replaces the one on screen, so the Script panel's observation openers appear. Verified, it reads "Opener
 * fact". Nothing without one.
 */
function ObservationNote(props: { business: Business; researched: boolean }) {
  const b = props.business;
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false); // Check clicked on this lead (keyed per business, so it resets)
  const line = observationLine(b, { researched: props.researched });
  if (!line) return null;
  const gate = observationUseGate(opened, busy);

  const use = async (e: Event) => {
    (e.currentTarget as HTMLElement).blur();
    if (!gate.enabled || !b.observation || !b.observationSourceUrl) return;
    setBusy(true);
    try {
      const r = await verifyObservation(b.id, { observation: b.observation, observationSourceUrl: b.observationSourceUrl });
      const cur = getDialer().item?.business;
      if (r && r.id === b.id) replaceBusiness(r);
      else if (cur && cur.id === b.id) replaceBusiness({ ...cur, observationVerifiedAt: new Date().toISOString() });
      toast('Verified. The Script panel can now open with it.', 'ok');
    } catch (err) {
      toast(`Not verified: ${errorMessage(err)}`, 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class={`vd-observation ${line.verified ? 'vd-observation-ok' : ''}`}>
      <b class="vd-observation-label" title={line.verified && b.observationVerifiedAt ? `Verified ${timeAgo(b.observationVerifiedAt)}` : undefined}>{line.label}:</b>
      <span class="vd-observation-text">{line.text}</span>
      {/* checkUrl is http(s) only (observationLine); a click or middle-click counts as opened, like the drawer's evidence. */}
      {line.checkUrl ? (
        <a href={line.checkUrl} target="_blank" rel="noopener noreferrer" title={OBSERVATION_CHECK_TITLE}
          onClick={(e) => { setOpened(true); (e.currentTarget as HTMLElement).blur(); }} onAuxClick={(e) => { if (e.button === 1) setOpened(true); }}>Check</a>
      ) : null}
      {line.canUse ? (
        <button type="button" class="btn btn-sm" disabled={!gate.enabled} title={gate.title} onClick={(e) => void use(e)}>
          {busy ? 'Saving…' : 'Use in opener'}
        </button>
      ) : null}
      {line.canUse && gate.hint ? <span class="muted">{gate.hint}</span> : null}
      {line.note ? <span class="muted">{line.note}</span> : null}
    </div>
  );
}

function drawerTitle(d: { data: unknown; loading: boolean; error: string | null } | null): string {
  if (!d) return 'Business info: map, website, reviews, people (I)';
  if (d.data) return 'Business info is ready (I)';
  if (d.loading) return 'Business info is loading (I)';
  return d.error ?? 'Business info (I)';
}

/**
 * The state's tip (QueueItem.stateNote), full width under the call bar and above the lead card and script, so it is on
 * screen before Dial at every width and stays there during the call: e.g. "Illinois: Tip: after your intro, ask…".
 * A recommendation since 2026-10-03 (PJ), not a line the setter must say; the hard rules are enforced by the dialer.
 */
export function StateNote(props: { item: QueueItem; loading?: boolean }) {
  const line = stateNoteLine(props.item.business, props.item.stateNote);
  if (!line) return null;
  return (
    <div class={`vd-state-note ${props.loading ? 'vd-loading' : ''}`} role="note" aria-label={`${line.place} tip for this call (recommended)`}>
      <span class="eyebrow">Tip for this state (recommended)</span>
      <p><b>{line.place}:</b> {line.text}</p>
    </div>
  );
}

export function leadPhone(item: QueueItem): string {
  return fmtPhone(item.business.phoneE164) + (item.business.phoneExt ? ` ext. ${item.business.phoneExt}` : '');
}
